import {
  ActionIcon,
  Center,
  Group,
  Loader,
  Pagination,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconEdit, IconEyeSearch, IconTrash } from "@tabler/icons-react";
import { safeApiCall } from "api/common.service";
import { deleteAdminDebitJnl, getDebitJournals } from "api/debitjnl.service";
import { logError } from "api/diagnostic.service";
import {
  MantineNotificationColor,
  MantineNotificationPosition,
  MSG_DEFAULT_FORM_DATA_LOAD_ERROR,
  MSG_DEFAULT_FORM_SUBMIT_SUCCESS,
  MSG_DEFAULT_FORM_SUBMIT_TITLE_DELETE,
} from "common/constants";
import { navigateTo } from "common/navigation";
import { confirmDelete, formatDate, formatNZD } from "common/utils";
import { DEFAULT_PAGE_SIZE } from "components/layouts/consts";
import DefaultButtonAdd from "pages/admin/components/DefaultButtonAdd";
import Reprint from "pages/admin/components/Reprint";
import { FullScreenModal } from "pages/modals";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PaginatedJournals } from "types/model.types";
import AdminDebitJournalEntryPage from "./AdminDebitJournalEntryPage";
import AdminDebitJournalPreview from "./AdminDebitJournalPreview";
import { DefaultTableLayout } from "./TableGridDefinitions";
const AdminDebitJournalPage: React.FC = () => {
  const [params] = useSearchParams();
  const id = params.get("id");
  const navigate = useNavigate();
  const [journalId, setJournalId] = useState<number | null>(null);
  const [preview, setPreview] = useState(false);
  const [journals, setJournals] = useState<PaginatedJournals>({
    data: [],
    total: 0,
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [entryModalOpen, setEntryModalOpen] = useState(false);
  const [reloadTrigger, setReloadTrigger] = useState(new Date());
  const [loading, setLoading] = useState<boolean>(true);
  const [activePage, setActivePage] = useState(1);

  useEffect(() => {
    let mounted = true;
    setLoading(true);

    const fetchData = async () => {
      try {
        const response = await safeApiCall(
          getDebitJournals(activePage, DEFAULT_PAGE_SIZE),
        );
        if (mounted && response) setJournals(response);
      } catch (err) {
        logError(err);
        notifications.show({
          message: MSG_DEFAULT_FORM_DATA_LOAD_ERROR,
          position: MantineNotificationPosition.TopCenter,
          color: MantineNotificationColor.Error,
        });
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchData();
    return () => {
      mounted = false;
    };
  }, [activePage, reloadTrigger]);

  const handlePrint = (journalId: string | number) => {
    // setJournalId(Number(journalId));
    // setPreview(true);
    navigateTo(`/print/dbjnl?id=${journalId}`);
  };

  const handleEdit = async (journalId: string | number) => {
    setJournalId(Number(journalId));
    setEntryModalOpen(true);
  };

  const handleDelete = async (id: string | number) => {
    if (!id) return;
    if (confirmDelete() === false) return;
    try {
      await safeApiCall(deleteAdminDebitJnl(Number(id)));
      setReloadTrigger(new Date());
      notifications.show({
        title: MSG_DEFAULT_FORM_SUBMIT_TITLE_DELETE,
        message: MSG_DEFAULT_FORM_SUBMIT_SUCCESS,
        position: MantineNotificationPosition.TopCenter,
        color: MantineNotificationColor.Success,
      });
    } catch {
      notifications.show({
        title: MSG_DEFAULT_FORM_SUBMIT_TITLE_DELETE,
        message: MSG_DEFAULT_FORM_DATA_LOAD_ERROR,
        position: MantineNotificationPosition.TopCenter,
        color: MantineNotificationColor.Error,
      });
    }
  };
  useEffect(() => {
    if (!id) return;

    handleEdit(id);
    setTimeout(() => {
      navigate("/admin/debit-journals", { replace: true });
    }, 500);
  }, [id]);

  const rows = journals.data.map((journal) => {
    return (
      <Table.Tr key={journal.id}>
        <Table.Td className="action-cell action-cell-lg">
          <Group gap="xs" wrap="nowrap">
            <Tooltip label="Print">
              <ActionIcon
                variant="subtle"
                onClick={() => handlePrint(journal.id)}
              >
                <IconEyeSearch size={18} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label={journal.isProcessed ? "Edit (disabled)" : "Edit"}>
              <ActionIcon
                variant="subtle"
                color="blue"
                disabled={journal.isProcessed}
                onClick={() => handleEdit(journal.id)}
              >
                <IconEdit size={18} />
              </ActionIcon>
            </Tooltip>
            <Tooltip
              label={journal.isProcessed ? "Delete (disabled)" : "Delete"}
            >
              <ActionIcon
                variant="subtle"
                color="red"
                disabled={journal.isProcessed}
                onClick={() => handleDelete(journal.id)}
              >
                <IconTrash size={18} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Table.Td>
        <Table.Td>{journal.id}</Table.Td>
        <Table.Td>{formatDate(journal.createdOn)}</Table.Td>
        <Table.Td className="money">{formatNZD(journal.expected)}</Table.Td>
        <Table.Td className="money">{formatNZD(journal.entered)}</Table.Td>
        <Table.Td className="notes">{journal.notes}</Table.Td>
      </Table.Tr>
    );
  });

  return (
    <Stack>
      <Title order={2}>Debit Journals</Title>
      {loading ? (
        <Center>
          <Loader />
        </Center>
      ) : (
        <>
          <Group justify="space-between" wrap="nowrap" mb="md">
            <Group justify="flex-start" wrap="nowrap" mb="md">
              <Reprint
                onReprint={(id: number) => {
                  handlePrint(id);
                }}
              />
              <DefaultButtonAdd
                text="Add Journal"
                onClick={() => {
                  setJournalId(null);
                  setEntryModalOpen(true);
                }}
              />
            </Group>

            <Group justify="flex-end" wrap="nowrap" mb="md">
              <Text size="sm" c="dimmed">
                Showing 30 items per page
              </Text>
              <Pagination
                total={Math.ceil(journals.total / DEFAULT_PAGE_SIZE)}
                value={activePage}
                onChange={setActivePage}
                siblings={1}
                boundaries={1}
              />
            </Group>
          </Group>
          <DefaultTableLayout>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ whiteSpace: "nowrap" }}>Actions</Table.Th>
                <Table.Th>Journal #</Table.Th>
                <Table.Th>Date</Table.Th>
                <Table.Th className="money">Expected</Table.Th>
                <Table.Th className="money">Entered</Table.Th>
                <Table.Th style={{ minWidth: 300 }}>Notes</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{rows}</Table.Tbody>
          </DefaultTableLayout>
          <Group justify="flex-end" wrap="nowrap" mb="md">
            <Pagination
              total={Math.ceil(journals.total / DEFAULT_PAGE_SIZE)}
              value={activePage}
              onChange={setActivePage}
              siblings={1}
              boundaries={1}
            />
          </Group>
        </>
      )}

      <FullScreenModal
        opened={entryModalOpen}
        onClose={() => {
          setEntryModalOpen(false);
          setReloadTrigger(new Date());
        }}
        title="Add|Edit Journal Entry"
      >
        <AdminDebitJournalEntryPage
          recordId={journalId}
          onSaveComplete={(close: boolean) => {
            setReloadTrigger(new Date());
            if (close) setEntryModalOpen(false);
          }}
        />
      </FullScreenModal>
      <FullScreenModal opened={preview} onClose={() => setPreview(false)}>
        {journalId && <AdminDebitJournalPreview id={journalId} />}
      </FullScreenModal>
    </Stack>
  );
};
export default AdminDebitJournalPage;
